/**
 * Bubble level of detail + viewport culling (laptop DOM view).
 *
 * Applied on every transform change, as pure math over a prebuilt cache (no
 * layout reads); DOM writes only where something changes:
 *   - a bubble too small or fully outside the visible rect (+25% margin) is
 *     not displayed, nor anything inside it; a few pixels wide, it is a dot;
 *     one whose inner bubbles are all dots (or not drawn) is filled with its
 *     color (lod-filled), as a leaf;
 *   - a leaf's name (HTML inside it) is hidden below reading size, and
 *     outside the visible rect. CSS reveals the name of a hovered or
 *     selected bubble regardless.
 * Parents' titles are not in the DOM: titles.js draws them on a canvas
 * (shownParents).
 *
 * This module deliberately imports nothing from the bubble view so both
 * physics.js and state.js can depend on it without a cycle.
 */

// World-space font size of .bubble-leaf-label — keep in sync with main.css.
const LEAF_FONT_WORLD_PX = 14;

// Below this size on screen (px) a node's title is not shown: leaves' names
// (14px in the map's units, CSS .bubble-leaf-label: from about 29%, all at
// the same zoom) and parents' titles (titles.js) alike. Alex, 2026-10-27:
// "needs to be visible down to 35%", then "nodes could still display titles
// at smaller scales, say 4px". It was 6 px (leaves' names from 43%).
export const MIN_TITLE_SCREEN_PX = 4;

// Bubbles too: one no wider than this on screen (px) is not drawn, nor
// anything inside it (its children are inside its element), and neither is
// one fully outside the visible rect. Alex (2026-10-15): "painted until they
// are 1px or less", "only paint what is on screen". At
// the whole-map zoom of 20,000 items every one of them was laid out and
// painted: a wheel step took about 4 s. Top-level bubbles too ("stop drawing
// a node when it is 1px only").
const MIN_BUBBLE_SCREEN_PX = 1;

// Visible-rect margin as a fraction of the viewport, per side.
const VIEWPORT_MARGIN = 0.25;

// NOTE: per-label compositor layers (will-change on the visible labels, then
// curved SVG titles in the DOM) were tried here and measured WORSE than plain visibility LOD — three
// variants (whole-layer, per-label capped, byte-budgeted smallest-first)
// all lost to no-layers at 4x throttle, because JS-driven zoom re-rasters
// every layer each frame anyway and threshold-crossing labels churn layer
// creation. It is also the mechanism behind the 2026-08 blank-rectangle
// GPU incident. Don't reintroduce it; the win comes from painting fewer
// labels, not from caching them.

let entries = [];
// While a large map's layout is animated, bubbles under MIN_MOVING_SCREEN_PX
// (the plain dots) are not drawn: each frame paints what is displayed, and a
// large map seen whole displays thousands of dots (20,000 items: about 48 ms
// of main-thread work a frame for a drag release, 2026-10-15). Alex: lower
// detail while moving, full detail when it settles.
let inMotion = false;

/** A large layout's motion starts or ends (physics.js): dots go or come back. */
export function setMotion(on) {
  if (inMotion === on) return;
  inMotion = on;
  if (lastTransform) apply(lastTransform.panX, lastTransform.panY, lastTransform.zoom);
}

let viewW = 0;
let viewH = 0;

/** The canvas size read by the last render (before it added bubbles). */
export function viewSize() {
  return { width: viewW, height: viewH };
}
let lastTransform = null; // {panX, panY, zoom} from the most recent apply()

/**
 * Absolute world center of a physics node.
 */
export function worldCenter(n) {
  let x = n.offsetX + n.x;
  let y = n.offsetY + n.y;
  let p = n.parent;
  while (p) {
    x += p.offsetX + p.x - p.radius;
    y += p.offsetY + p.y - p.radius;
    p = p.parent;
  }
  return { x, y };
}

/**
 * Rebuild the label cache from the current physics nodes. Call after every
 * full DOM render (elements are recreated). No-op cache in canvas mode —
 * callers pass the DOM-mode nodes only.
 *
 * @param {Array} physicsNodes - live physics nodes (carry el / labelSvg)
 * @param {HTMLElement} canvasEl - .bubble-canvas, for the viewport size
 * @param {{width: number, height: number}} [size] - the canvas size, read
 *   before the render added the bubbles: reading it after forced the
 *   browser to style and lay out every new bubble at once (about 1 s at
 *   27,000 items), then again for the next frame
 */
export function rebuild(physicsNodes, canvasEl, size) {
  entries = [];
  if (size) {
    viewW = size.width || 0;
    viewH = size.height || 0;
  } else if (canvasEl) {
    viewW = canvasEl.clientWidth || 0;
    viewH = canvasEl.clientHeight || 0;
  }
  for (const n of physicsNodes) {
    if (!n.el) continue; // canvas mode — no DOM labels

    entries.push({
      node: n,
      el: n.el,
      bubble: true,
      r: n.radius,
      // Its largest inner bubble (render.js links the children): all are
      // hidden when that one is.
      maxChild: (n.children || []).reduce((m, c) => Math.max(m, c.radius), 0),
      minSize: MIN_BUBBLE_SCREEN_PX,
      cx: 0,
      cy: 0,
      shown: !n.el.classList.contains("lod-hidden"),
      dot: n.el.classList.contains("lod-dot"),
      filled: n.el.classList.contains("lod-filled"),
    });

    // render.js records the leaf's label (centeredLabel); older nodes are queried.
    const centered = n.centeredLabel !== undefined ? n.centeredLabel : n.el.querySelector(":scope > .bubble-label-centered");
    if (centered) {
      entries.push({
        node: n,
        el: centered,
        r: n.radius,
        fontWorld: LEAF_FONT_WORLD_PX,
        minFont: MIN_TITLE_SCREEN_PX,
        cx: 0,
        cy: 0,
        shown: centered.style.visibility !== "hidden",
      });
    }
  }
  refreshPositions();
}

/**
 * Recompute cached world positions from the physics nodes and re-apply.
 * Called when the simulation settles (labels moved, transform didn't).
 */
export function refreshPositions() {
  for (const e of entries) {
    const c = worldCenter(e.node);
    e.cx = c.x;
    e.cy = c.y;
  }
  if (lastTransform) {
    apply(lastTransform.panX, lastTransform.panY, lastTransform.zoom);
  }
}

/**
 * Apply LOD + culling for the given transform. Pure math over the cache;
 * writes style.visibility only on state flips.
 */
export function apply(panX, panY, zoom) {
  lastTransform = { panX, panY, zoom };
  if (!entries.length || !zoom) return;

  const mx = viewW * VIEWPORT_MARGIN;
  const my = viewH * VIEWPORT_MARGIN;
  const wx0 = (-panX - mx) / zoom;
  const wy0 = (-panY - my) / zoom;
  const wx1 = (viewW - panX + mx) / zoom;
  const wy1 = (viewH - panY + my) / zoom;

  for (const e of entries) {
    const inView = e.cx + e.r > wx0 && e.cx - e.r < wx1 && e.cy + e.r > wy0 && e.cy - e.r < wy1;
    if (e.bubble) {
      // A bubble being dragged stays, whatever its size or place.
      const minSize = inMotion ? MIN_MOVING_SCREEN_PX : e.minSize;
      const want = (inView && 2 * e.r * zoom > minSize) || e.el.classList.contains("dragging");
      if (want !== e.shown) {
        e.shown = want;
        e.el.classList.toggle("lod-hidden", !want);
      }
      // Under a few pixels a bubble is drawn as a plain dot (CSS lod-dot): its
      // shadow and ring cannot be seen there, and painting them for the
      // thousands of such bubbles of a large map seen whole made every frame
      // of its layout's animation cost about 50 ms (2026-10-15).
      const dot = want && 2 * e.r * zoom < MIN_MOVING_SCREEN_PX;
      if (dot !== e.dot) {
        e.dot = dot;
        e.el.classList.toggle("lod-dot", dot);
      }
      // Its inner bubbles all dots, or too small to be drawn: filled with its
      // color, else a large map seen whole was dark discs with specks in
      // them. (When they are under a pixel, it is a dot itself already.)
      const filled = want && !dot && e.maxChild > 0 && 2 * e.maxChild * zoom < MIN_MOVING_SCREEN_PX;
      if (filled !== e.filled) {
        e.filled = filled;
        e.el.classList.toggle("lod-filled", filled);
      }
      continue;
    }
    const want = e.fontWorld * zoom >= e.minFont && inView;
    if (want !== e.shown) {
      e.shown = want;
      // visibility, not display: CSS reveals it on hover and selection.
      e.el.style.visibility = want ? "" : "hidden";
    }
  }
}

// A bubble narrower than this on screen (px) is not worth animating: its
// motion cannot be seen. A large map's layout computes such groups without
// drawing (physics.js), and they ride with their parent.
const MIN_MOVING_SCREEN_PX = 8;

/**
 * Whether a bubble's motion can be seen with this view: at least
 * MIN_MOVING_SCREEN_PX across and inside the visible rect (+ margin). Also
 * in canvas mode (physics.js: which groups a large map's layout animates).
 * @param {Object} n - physics node
 * @param {{zoom:number, panX:number, panY:number}} t - view
 * @param {number} width - canvas width (px)
 * @param {number} height - canvas height (px)
 */
export function movesVisibly(n, t, width, height) {
  if (!(2 * n.radius * t.zoom >= MIN_MOVING_SCREEN_PX)) return false;
  const c = worldCenter(n);
  const sx = t.panX + c.x * t.zoom;
  const sy = t.panY + c.y * t.zoom;
  const r = n.radius * t.zoom;
  const mx = width * VIEWPORT_MARGIN;
  const my = height * VIEWPORT_MARGIN;
  return sx + r > -mx && sx - r < width + mx && sy + r > -my && sy - r < height + my;
}

/** The displayed parent bubbles, whose titles titles.js draws. */
export function shownParents() {
  const out = [];
  for (const e of entries) {
    if (e.bubble && e.shown && !e.node.isLeaf) out.push(e.node);
  }
  return out;
}

/**
 * Drop the cache (canvas mode / view teardown).
 */
export function clear() {
  entries = [];
  lastTransform = null;
}
